import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../common/api';
import clienteAuthService from '../api/authService';
import ordiniService from '../api/clienteService';
import IndirizzoForm from '../components/IndirizzoForm';
import PartnerRecruitmentBanner from '../components/PartnerRecruitmentBanner';
import NewsletterSignup from '../components/NewsletterSignup';
import DocumentHead from '../components/DocumentHead';
import { useShopConfig } from '../hooks/useShopConfig';
import {
  loadCarrello, updateQuantita, removeFromCarrello, clearCarrello,
} from '../hooks/carrelloStorage';

function euro(v) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

export default function Carrello() {
  const navigate = useNavigate();
  const { catalogo_live: catalogoLive, catalogo_preview: catalogoPreview } = useShopConfig();
  const [items, setItems] = useState(loadCarrello());
  const [indirizzo, setIndirizzo] = useState({ via: '', citta: '', cap: '', lat: null, lng: null });
  const [dataConsegna, setDataConsegna] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(clienteAuthService.getCurrentUser());

  useEffect(() => {
    if (items.length === 0) return;
    const currentUser = clienteAuthService.getCurrentUser();
    if (currentUser?.indirizzo_predefinito) {
      setIndirizzo((a) => ({ ...a, ...currentUser.indirizzo_predefinito }));
    }
    
    if (clienteAuthService.isAuthenticated()) {
      import('../../common/axios').then(({ clienteApi }) => {
        clienteApi.get('/cliente/me').then((res) => {
          if (res.data.user) {
            setUser(res.data.user);
            clienteAuthService.setCurrentUser(res.data.user);
          }
        });
      });
    }
  }, [items.length]);

  const totaleStimato = items.reduce((s, i) => s + (i.prezzo_piatto || 0) * i.quantita, 0);
  const creditoDisponibile = user && parseFloat(user.saldo_credito) > 0 ? parseFloat(user.saldo_credito) : 0;
  const daPagareStimato = Math.max(0, totaleStimato - creditoDisponibile);

  const checkout = async () => {
    if (!clienteAuthService.isAuthenticated()) {
      navigate('/area-riservata/login', { state: { from: '/carrello' } });
      return;
    }
    if (!indirizzo.via || !indirizzo.citta || !indirizzo.cap) {
      setError('Compila l\'indirizzo di consegna');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await ordiniService.create({
        items: items.map((i) => ({
          categoria_catalogo_id: i.categoria_catalogo_id,
          quantita: i.quantita,
        })),
        indirizzo_consegna: indirizzo,
        data_consegna_richiesta: dataConsegna || undefined,
      });
      const ordini = res.data.ordini || [];
      clearCarrello();
      const primo = ordini.find((o) => o.stato === 'in_attesa_pagamento') || ordini[0];
      if (!primo) throw new Error('Nessun ordine creato');

      const checkoutRes = await fetch(buildApiUrl('/api/cliente/pagamenti/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cliente_token')}`,
        },
        body: JSON.stringify({
          ordine_id: primo.id,
          success_url: `${window.location.origin}/area-riservata?pagamento=ok&ordini=${ordini.length}`,
          cancel_url: `${window.location.origin}/carrello?pagamento=cancel`,
        }),
      });
      const checkout = await checkoutRes.json();
      if (checkout.pagato_con_credito) {
        // Refresh saldo locale
        try {
          const { clienteApi } = await import('../../common/axios');
          const me = await clienteApi.get('/cliente/me');
          if (me.data.user) clienteAuthService.setCurrentUser(me.data.user);
        } catch (_) { /* ignore */ }
        navigate('/area-riservata?pagamento=ok&credito=1');
        return;
      }
      if (checkout.checkout_url) {
        if (ordini.length > 1) {
          sessionStorage.setItem('ordini_da_pagare', JSON.stringify(
            ordini.filter((o) => o.id !== primo.id).map((o) => o.id)
          ));
        }
        window.location.href = checkout.checkout_url;
      } else {
        setError(checkout.message || 'Errore avvio pagamento');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Errore checkout');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FFFEFA] flex flex-col items-center justify-center px-4">
        <p className="text-gray-500 mb-4">Il carrello è vuoto</p>
        <Link to="/catalogo" className="text-[#c78244] underline">Vai al catalogo</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFEFA] py-8 px-4">
      <DocumentHead
        title="Carrello"
        description="Il tuo carrello Mangitalia — eccellenze alimentari italiane selezionate."
        path="/carrello"
      />
      {catalogoPreview && <div className="max-w-3xl mx-auto mb-4"><PartnerRecruitmentBanner compact catalogoPreview /></div>}
      {!catalogoPreview && <div className="max-w-3xl mx-auto mb-4"><PartnerRecruitmentBanner compact /></div>}
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Carrello</h1>
          <Link to="/catalogo" className="text-sm text-[#c78244] underline">← Continua acquisti</Link>
        </div>

        <div className="space-y-4 mb-8">
          {items.map((item) => (
            <div key={item.categoria_catalogo_id} className="mang-card p-4 flex flex-wrap justify-between gap-3 items-center">
              <div>
                <div className="font-semibold">{item.nome}</div>
                <div className="text-sm text-gray-500">{euro(item.prezzo_piatto)} / unità</div>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" min={1} max={999} className="w-16 border rounded-lg px-2 py-1 text-sm"
                  value={item.quantita}
                  onChange={(e) => setItems(updateQuantita(item.categoria_catalogo_id, parseInt(e.target.value, 10) || 1))} />
                <button type="button" className="text-red-500 text-sm" onClick={() => setItems(removeFromCarrello(item.categoria_catalogo_id))}>
                  Rimuovi
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mang-card p-5 space-y-4">
          <h2 className="font-semibold">Consegna</h2>
          <IndirizzoForm value={indirizzo} onChange={setIndirizzo} />
          <div>
            <label className="block text-sm font-medium mb-1">Data consegna desiderata</label>
            <input type="date" className="w-full border rounded-xl px-4 py-2.5 text-sm" value={dataConsegna}
              onChange={(e) => setDataConsegna(e.target.value)} />
          </div>
          {creditoDisponibile > 0 && (
            <div className="flex justify-between text-sm pt-2 text-green-600">
              <span>Credito piattaforma (stimato)</span>
              <span>- {euro(Math.min(creditoDisponibile, totaleStimato))}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg pt-2 border-t">
            <span>Da pagare stimato</span>
            <span className="text-[#c78244]">{euro(daPagareStimato)} + trasporto</span>
          </div>
          {creditoDisponibile > 0 && (
            <p className="text-xs text-gray-500">
              Il credito viene scalato automaticamente al checkout sul totale ordine (prodotti + trasporto).
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {catalogoPreview && (
            <div className="space-y-3 bg-[#fff8ea] border border-[#f3deb8] rounded-xl px-4 py-3">
              <p className="text-sm text-[#7a4a1f]">
                Catalogo in anteprima: il checkout sarà attivo al lancio ufficiale.
                Lascia l&apos;email e ti avvisiamo.
              </p>
              <NewsletterSignup sorgente="carrello" interesse="checkout" compact />
            </div>
          )}
          <p className="text-xs text-gray-500">
            {items.length > 1
              ? 'Verranno creati ordini separati; pagherai il primo ora, gli altri dall\'area riservata.'
              : 'Il trasporto viene calcolato in base all\'indirizzo.'}
          </p>
          <button type="button" disabled={loading || !catalogoLive} onClick={checkout}
            className="w-full bg-[#c78244] text-white py-3 rounded-xl font-semibold disabled:opacity-50">
            {loading ? 'Elaborazione…' : catalogoLive ? 'Procedi al pagamento' : 'Checkout in arrivo al lancio'}
          </button>
        </div>
      </div>
    </div>
  );
}
